import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { fileUrl } from "@/lib/api";
import { X, List } from "lucide-react";

const employeeNav = [
  { to: "/attendance", label: "Absensi", icon: "schedule" },
  { to: "/dashboard", label: "Gaji Saya", icon: "payments" },
  { to: "/reimbursement", label: "Reimbursement", icon: "receipt_long" },
  { to: "/cash-advance", label: "Kasbon", icon: "account_balance_wallet" },
  { to: "/rfq", label: "RFQ", icon: "request_quote" },
  { to: "/profile", label: "Profil Saya", icon: "badge" },
];

const adminNavFull = [
  { to: "/admin", label: "Data Karyawan", icon: "groups", end: true },
  { to: "/admin/attendance", label: "Absensi Karyawan", icon: "how_to_reg" },
  { to: "/admin/rfqs", label: "RFQ Management", icon: "assignment" },
];

const adminNavLimited = [
  { to: "/admin/attendance", label: "Absensi Karyawan", icon: "how_to_reg" },
  { to: "/admin/rfqs", label: "RFQ Management", icon: "assignment" },
];

function NavGroup({ title, items, onNavigate }) {
  return (
    <div>
      <p className="skn-section-label px-3 mb-1">{title}</p>
      <nav className="flex flex-col gap-1">
        {items.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={onNavigate}
            data-testid={`nav-${item.to.replace(/\//g, "") || "home"}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-lg text-[14px] font-medium transition-colors ${
                isActive
                  ? "bg-[#1b4d68] text-white shadow-sm"
                  : "text-[#41484d] hover:bg-[#e5eeff] hover:text-[#0b1c30]"
              }`
            }
          >
            <span className="material-symbols-outlined text-[20px]">{item.icon}</span>
            {item.label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}

export function AppLayout({ children, title }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const isPrimaryAdmin = user?.email?.toLowerCase() === "general@samitragroup.com";
  const isAdmin = user?.role === "admin";

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const SidebarInner = () => (
    <div className="flex flex-col h-full">
      <div className="h-16 flex items-center gap-2 px-4 border-b border-[#E2E8F0]/80 bg-white/50">
        <img src="/logo-samitra.webp" alt="Samitra Karya Nusantara" className="h-8 w-auto object-contain" data-testid="app-logo" />
        <div className="flex flex-col">
          <span className="text-[16px] leading-tight font-bold text-[#00364e]" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>SKN Portal</span>
          <span className="text-[12px] leading-none text-[#64748B]">HR &amp; Operations</span>
        </div>
      </div>
      <div className="flex-1 px-2 py-4 space-y-4 overflow-y-auto">
        {!isAdmin ? (
          <NavGroup title="Panel Karyawan" items={employeeNav} onNavigate={() => setOpen(false)} />
        ) : (
          <NavGroup
            title={isPrimaryAdmin ? "Panel Admin" : "Panel Admin"}
            items={isPrimaryAdmin ? adminNavFull : adminNavLimited}
            onNavigate={() => setOpen(false)}
          />
        )}

      </div>
      <div className="p-2 border-t border-[#E2E8F0]/80 bg-white/60">
        <div className="flex items-center justify-between p-1 rounded-xl bg-white hover:bg-[#eff4ff] transition-colors">
          <div className="flex items-center gap-2 min-w-0">
            {user?.avatar_url ? (
              <img src={fileUrl(user.avatar_url)} alt={user.name} className="h-8 w-8 rounded-full object-cover flex-shrink-0" />
            ) : (
              <div className="h-8 w-8 rounded-full bg-[#00364e] text-white flex items-center justify-center flex-shrink-0">
                <span className="material-symbols-outlined text-[18px]">person</span>
              </div>
            )}
            <div className="min-w-0 pr-1">
              <p className="text-[14px] font-semibold truncate leading-tight text-[#0b1c30]" data-testid="sidebar-user-name">{user?.name}</p>
              <p className="text-[11px] text-[#64748B] truncate leading-tight">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            title="Keluar"
            className="p-1.5 rounded-lg text-[#41484d] hover:text-[#DC2626] hover:bg-[#ffdad6] transition-colors flex items-center justify-center"
          >
            <span className="material-symbols-outlined text-[18px]">logout</span>
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-[#F8FAFC]">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 flex-col border-r border-[#E2E8F0]/80 bg-white/[0.82] backdrop-blur-xl fixed inset-y-0 z-30">
        <SidebarInner />
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <aside className="absolute left-0 inset-y-0 w-64 bg-white/[0.95] border-r border-[#E2E8F0]/80 animate-drawer-in shadow-2xl">
            <SidebarInner />
          </aside>
        </div>
      )}

      <div className="flex-1 lg:ml-64 flex flex-col min-w-0">
        <header className="sticky top-0 z-30 h-16 bg-white/75 backdrop-blur-md border-b border-[#E2E8F0]/80 flex items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setOpen(true)} data-testid="mobile-menu-button">
              {open ? <X size={22} /> : <List size={22} />}
            </Button>
            <img src="/logo-samitra.webp" alt="Samitra Karya Nusantara" className="h-8 w-auto object-contain" />
            <span className="text-[16px] font-bold text-[#00364e] hidden sm:inline-block" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>Samitra Karya Nusantara</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden md:flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-[#00364e] text-white flex items-center justify-center">
                <span className="material-symbols-outlined text-[18px]">person</span>
              </div>
              <div className="flex flex-col text-left">
                <span className="text-[14px] font-semibold leading-tight text-[#0b1c30]">{user?.name}</span>
                <span className="text-[11px] text-[#64748B] leading-tight">{user?.position || (isAdmin ? "Administrator" : "Karyawan")}</span>
              </div>
            </div>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-8">
          {title ? <h1 className="sr-only">{title}</h1> : null}
          {children}
        </main>
      </div>
    </div>
  );
}
